import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService, type JoinInput } from '../services/auth.service';
import { meService } from '../services/me.service';
import { HandleSchema } from '../schemas/auth.schema';
import { useAuthStore } from '../stores/authStore';

export const useInvite = (code: string | undefined) =>
    useQuery({
        queryKey: ['invite', code],
        queryFn: () => authService.invite(code as string),
        enabled: code !== undefined && code.length > 0,
        retry: false,
        // The roster changes as friends sign up, and this screen is open while
        // that is happening.
        staleTime: 10_000,
    });

/** The roster for PIN sign-in, read through a link the user already holds. */
export const useRoster = (code: string | undefined) => useInvite(code);

export const useJoin = () => {
    const setSession = useAuthStore((s) => s.setSession);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: JoinInput) => authService.join(input),
        onSuccess: (session) => {
            setSession(session);
            void queryClient.invalidateQueries({ queryKey: ['me'] });
        },
    });
};

export const useSignInWithPin = () => {
    const setSession = useAuthStore((s) => s.setSession);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (vars: { userId: number; pin: string }) =>
            authService.signInWithPin(vars.userId, vars.pin),
        onSuccess: (session) => {
            setSession(session);
            void queryClient.invalidateQueries({ queryKey: ['me'] });
        },
    });
};

export const useLogout = () => {
    const clearSession = useAuthStore((s) => s.clearSession);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => authService.logout(),
        onSuccess: () => {
            clearSession();
            // Everything cached belonged to the session that just ended, and
            // signing out navigates away from every observer, so clearing here
            // cannot retrigger a fetch.
            queryClient.clear();
        },
    });
};

export const useRename = () => {
    const setSession = useAuthStore((s) => s.setSession);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (handle: string) => meService.rename(HandleSchema.parse(handle)),
        onSuccess: (user) => {
            setSession({ id: user.id, handle: user.handle, isAdmin: user.isAdmin });
            void queryClient.invalidateQueries({ queryKey: ['me'] });
            void queryClient.invalidateQueries({ queryKey: ['hangouts'] });
        },
    });
};
